import React, { useState, useCallback, useEffect, useMemo } from "react";
import { DatePicker, Icon, Popover, TextField } from "@medentic/polaris-react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { CalendarIcon } from "@medentic/polaris-icons";
import { useTranslation } from "react-i18next";
import { isValidDate } from "./utils";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(localizedFormat);

const DB_DATE_FORMAT_STRING = "YYYY-MM-DD";

interface DatePickerFieldProps {
  value: string;
  onChange: (value: string) => void;
  onInputChange?: (value: string) => void;
  label: string;
  alignment?: "left" | "center" | "right";
  disabled?: boolean;
  isRequired?: boolean;
  error?: string;
}

const DatePickerField: React.FC<DatePickerFieldProps> = ({
  value,
  onChange,
  onInputChange,
  label,
  alignment = "left",
  disabled = false,
  isRequired = false,
  error,
}) => {
  const { i18n, t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [inputValue, setInputValue] = useState<string>("");

  const [{ month, year }, setDate] = useState({ month: 10, year: 2022 });
  const [active, setActive] = useState(false);
  const [selectedDate, setSelectedDate] = useState<{
    start: Date;
    end: Date;
  }>({
    start: dayjs().toDate(),
    end: dayjs().toDate(),
  });

  // Determine week start day based on locale
  // 0 = Sunday (US, Canada, Japan, Israel)
  // 1 = Monday (Most of Europe, Asia, Australia, Latin America)
  const weekStartsOn = useMemo(() => {
    const locale = i18n.language;
    // Locales that start week on Sunday
    const sundayStartLocales = ['en-US', 'en-CA', 'ja', 'he'];
    
    // For Bulgarian and most European languages, start on Monday
    if (locale === 'bg' || locale.startsWith('en-')) {
      // Default English to Monday (international standard)
      return 1;
    }
    
    return sundayStartLocales.includes(locale) ? 0 : 1;
  }, [i18n.language]);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    const dateRange = {
      start: value ? dayjs(value).toDate() : dayjs().toDate(),
      end: value ? dayjs(value).toDate() : dayjs().toDate(),
    };

    setSelectedDate(dateRange);
    setDate({
      month: dayjs(dateRange.start).month(),
      year: dayjs(dateRange.start).year(),
    });
  }, [value]);

  const handleChange = useCallback(() => setActive((prev) => !prev), []);

  const handleMonthChange = (month: number, year: number) => {
    setDate({ month, year });
  };

  const handleBlur = useCallback(() => {
    if (inputValue) {
      if (!isValidDate(inputValue)) {
        setErrorMessage(
          t("datePicker.validation.format", { format: DB_DATE_FORMAT_STRING })
        );
      } else {
        onChange(inputValue);
        setErrorMessage("");
      }
    }
  }, [inputValue, onChange, t]);

  const onInputValueChange = useCallback((value: string) => {
    setInputValue(value);
    setErrorMessage("");
    if (onInputChange && !isValidDate(value)) {
      onInputChange(value);
    }
  }, [onInputChange]);

  const activator = useMemo(
    () => (
      <div
        onClick={disabled ? undefined : handleChange}
        style={{ width: "100%" }}
      >
        <TextField
          role="combobox"
          label={label}
          prefix={<Icon source={CalendarIcon} />}
          value={inputValue}
          placeholder={t("datePicker.placeholder")}
          autoComplete="off"
          onBlur={handleBlur}
          onChange={onInputValueChange}
          error={error || errorMessage}
          disabled={disabled}
          requiredIndicator={isRequired}
          clearButton={true}
          onClearButtonClick={() => {
            setInputValue("");
            onChange("");
          }}
        />
      </div>
    ),
    [
      disabled,
      handleChange,
      handleBlur,
      onInputValueChange,
      inputValue,
      error,
      errorMessage,
      label,
      isRequired,
      onChange,
      t,
    ]
  );

  const onDateChange = ({ start }: { start: Date }) => {
    const dateStr = dayjs(start).format(DB_DATE_FORMAT_STRING);
    onChange(dateStr);
    setInputValue(dateStr);
    setErrorMessage("");
  };

  return (
    <Popover
      key="date-picker-button-key"
      activator={activator}
      active={active}
      onClose={handleChange}
      preferredAlignment={alignment}
    >
      <Popover.Section>
        <DatePicker
          month={month}
          year={year}
          onChange={onDateChange}
          selected={{
            start: new Date(selectedDate.start),
            end: new Date(selectedDate.end),
          }}
          onMonthChange={handleMonthChange}
          allowRange={false}
          weekStartsOn={weekStartsOn}
        />
      </Popover.Section>
    </Popover>
  );
};

export default DatePickerField;
